import { FC, PropsWithChildren, useEffect, useRef } from 'react';

import * as Sentry from '@sentry/react';
import { isDeepEqual } from 'remeda';

import { useErrorEffect } from '@/shared/lib/errors';
import { useAppDispatch, useAppSelector } from '@/shared/lib/redux';

import { TCastingsClassifiers } from '@/shared/types/classifiers';

import {
  updateCastingsClassifiers,
  useGetClassifiers
} from '@/entities/classifiers';
import { useRole } from '@/entities/user';

import { useSyncStorage } from '@/features/auth';

import { LayoutWithSidebar } from '@/widgets/layouts';
import { Navigation } from '@/widgets/sidebar';

import { Classifiers } from '../injections/Classifiers';
import { LoggedInUserApiInterceptions } from '../injections/LoggedInUserApiInterceptions';
import QueryParam from '../injections/QueryParam';

/**
 * Композиция для страницы с авторизованным пользователем
 * @param root0 Пропсы
 * @param root0.children Дочерние элементы
 */
export const LoggedInUserComposition: FC<PropsWithChildren> = ({
  children
}) => {
  const dispatch = useAppDispatch();
  const role = useRole();
  const user = useAppSelector(state => state.user.data);
  const prevClassifiers = useRef<TCastingsClassifiers | null>(null);

  useSyncStorage();

  const { error, castingsClassifiers, refetchCastingsClassifiers } =
    useGetClassifiers();

  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email || 'undefined',
        phone: user.phone
      });
    }
  }, [user]);

  useEffect(() => {
    if (
      castingsClassifiers &&
      !isDeepEqual(prevClassifiers.current, castingsClassifiers)
    ) {
      prevClassifiers.current = castingsClassifiers;
      dispatch(updateCastingsClassifiers(castingsClassifiers));
    }
  }, [castingsClassifiers]);

  useEffect(() => {
    if ((role === 'actor' || role === 'director') && !castingsClassifiers) {
      refetchCastingsClassifiers();
    }
  }, [castingsClassifiers, role]);

  useErrorEffect(error);
  return (
    <LoggedInUserApiInterceptions>
      <QueryParam>
        <Classifiers>
          <LayoutWithSidebar sidebar={<Navigation />}>
            {children}
          </LayoutWithSidebar>
        </Classifiers>
      </QueryParam>
    </LoggedInUserApiInterceptions>
  );
};
